import clsx from 'clsx'

import { Icon } from '@fv/client-components'
import type { ListShipment } from '@fv/client-types'

import { DividedContent } from '@/components/DividedContent'
import { Price } from '@/components/shared/Price'
import { ShipmentCardWorkflowLabel } from '@/features/shipment-list/ShipmentCardWorkflowLabel'
import { useRateModifier } from '@/hooks/useRateModifier'

import { usePermissions } from '../auth'
import { NewMessageIndicator } from './NewMessageIndicator'
import { VendorShipmentListIndicator } from './VendorShipmentIndicator'

type PendingShipmentCardHeaderProps = {
  shipment: ListShipment
}

const PendingShipmentCardHeader = ({
  shipment,
}: PendingShipmentCardHeaderProps) => {
  const {
    cheapestQuote,
    isLiveLoad,
    pickup,
    selectedQuote,
    status,
    workflow,
    isFreightCollect,
  } = shipment

  const { canViewRates } = usePermissions()
  const rateMod = useRateModifier()
  const quote = selectedQuote ?? cheapestQuote
  const displayQuote = quote
    ? rateMod(quote, workflow, shipment.exchangeRates)
    : undefined
  const isAwarded = status === 'awarded'
  const isPending = status === 'pending'

  return (
    <>
      <DividedContent className="flex items-center py-3 px-4 bg-white flex-1 overflow-hidden">
        <div>
          <ShipmentCardWorkflowLabel
            mode={shipment.equipment.mode}
            isMultiStop={shipment.locations.length > 2}
          />
        </div>

        <div className="truncate flex items-center">
          <span
            className={clsx(
              'w-6 h-6 mr-2 rounded-full border-2 flex items-center justify-center',
              {
                'bg-fv-beer-light border-fv-beer/40':
                  isAwarded && pickup?.status !== 'error',
                'bg-fv-green-100 border-fv-green-300': isPending,
                'bg-fv-red/30 border-fv-red/60': pickup?.status === 'error',
              },
            )}
          >
            {isAwarded &&
              (pickup?.status === 'error' ? (
                <Icon
                  icon="exclamation-triangle"
                  className="text-fv-gray-dark"
                  transform="shrink-2 up-1 right-.5"
                />
              ) : (
                <Icon
                  icon="trophy"
                  className="text-fv-gray-dark"
                  transform="shrink-1 down-1 right-.5"
                />
              ))}

            {isPending && (
              <Icon icon="dollar-sign" className="text-fv-gray-dark" />
            )}
          </span>
          <span className="truncate">
            {isAwarded && 'Awarded - '}

            {isAwarded &&
              (pickup?.status === 'error'
                ? 'booking failed'
                : 'awaiting confirmation')}

            {isPending && 'Receiving quotes'}
          </span>
        </div>
        {workflow === 'truckload' && (
          <div className="shipment-card__live-load whitespace-nowrap">
            <Icon
              className="text-fv-gray-dark"
              icon={isLiveLoad ? 'boxes' : 'list'}
            />
            <span>{isLiveLoad ? 'Live load' : 'Quote only'}</span>
          </div>
        )}
      </DividedContent>

      {shipment.createdBy.vendorId && (
        <VendorShipmentListIndicator name={shipment.vendor?.name} />
      )}
      <NewMessageIndicator loadId={shipment.loadId} />
      {canViewRates && !isFreightCollect ? (
        <div
          className={clsx(
            'flex px-4 items-center bg-fv-green-100 price font-oxygen border-fv-gray border-l w-44 rounded-tr-lg justify-end',

            {
              'not-available': !displayQuote,
            },
          )}
        >
          {displayQuote ? (
            <Price
              amount={displayQuote.amount}
              currency={displayQuote.currency ?? ''}
            />
          ) : (
            <span title="No quotes received">NA</span>
          )}
        </div>
      ) : (
        <div className="rounded-tr-lg flex w-2 bg-white"></div>
      )}
    </>
  )
}

export default PendingShipmentCardHeader
